import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import * as PIXI from "pixi.js";
import "leaflet-pixi-overlay";
import { useMap } from "react-leaflet";
import L from "leaflet";
import { useTheme } from "@mui/material";
import { useOutletContext } from "react-router-dom";
import drawMarker from "./components/drawMaker";
import isEqual from "fast-deep-equal/react";
import { useMapa } from "../../../../../contexts/mapaContext";
import Supercluster from "supercluster";

PIXI.settings.FAIL_IF_MAJOR_PERFORMANCE_CAVEAT = false;
PIXI.settings.ROUND_PIXELS = true;
PIXI.utils.skipHello();
PIXI.Ticker.shared.maxFPS = 30;

const { Assets } = PIXI;

function PixiOverlay({
  markers = [],
  enableClustering = true,
  onClustersChange,
  onClusterClick,
  elementSelected,
  editable,
  onClick = () => {},
  onBackgroundClick = () => {},
  handleUpdatePositionElement = () => {},
  iconsElements,
  disable = false,
  showLabel = false,
  onRadiusChange = () => {},
  rulerMode,
  zoom,
  editMode = false,
  configurationMap = {},
  configElementDataMap = new Map(),
}) {
  const { hostsDataMap } = useMapa();
  const { config, configLayer } = useOutletContext();
  const spriteByIdRef = useRef(new Map());
  const explosionsRef = useRef(new Map());

  const theme = useTheme();
  const colorMode = theme.palette.mode;
  const tema = configurationMap?.configuracoes?.temaElemento || "classico";

  /* ---------- STATE ---------- */
  const [texturesLoaded, setTexturesLoaded] = useState(false);
  const [pixiOverlay, setPixiOverlay] = useState(null);
  const [labelTextures, setLabelTextures] = useState(new Map());
  const tickerFantasmaRef = useRef(null);
  const scRef = useRef(null);
  const [clusters, setClusters] = useState([]);
  const MIN_ZOOM_CLUSTER = config?.configuracoes?.ping?.clusterPing ?? 16;
  const maxClusterRadius =
    38 * ((config?.configuracoes?.ping?.clusterArea ?? 5) / 2);

  /* Índice do Supercluster e mapeamento id->marker */
  const id2MarkerRef = useRef(new Map());

  /* Radio do node */
  const lastRadiusRef = useRef(null);

  /* ---------- MAP ---------- */
  const map = useMap();

  const textureKeys = useMemo(() => {
    const baseKeys = [
      ...new Set(markers.map((m) => m.iconId).filter(Boolean)),
    ].map((key) => key);

    let baseKey = [];
    if (elementSelected && elementSelected?.tempId) {
      baseKey = [elementSelected.img];
    }
    // filter(Boolean): caixas de texto (tipo 3) têm img null e não viram textura
    return [...baseKeys, ...baseKey].filter(Boolean);
  }, [markers, elementSelected]);

  /* ---------- EFFECT: carrega texturas novas ---------- */
  useEffect(() => {
    if (!textureKeys.length) return;
    setTexturesLoaded(false);

    const promises = textureKeys.map((texKey) => {
      if (Assets.cache.has(texKey)) return Promise.resolve();

      // segurança – se faltar PNG cria SVG genérico
      Assets.add(texKey, getDefaultIcon());

      if (texKey === "cluster_icon") {
        Assets.add(texKey, getClusterIcon());
      }
      return Assets.load(texKey);
    });

    Promise.all(promises)
      .then(() => setTexturesLoaded(true))
      .catch((err) => console.error("Falha ao carregar texturas:", err));
  }, [textureKeys]);

  /* ---------- BUILD: índice Supercluster (quando markers mudam) ---------- */
  useEffect(() => {
    if (!enableClustering || !markers.length) {
      scRef.current = null;
      id2MarkerRef.current = new Map();
      setClusters([]);
      return;
    }
    const features = [];
    const id2Marker = new Map();

    for (const m of markers) {
      if (
        !(
          m?.elementConfig?.config?.coordenadas?.lat &&
          m?.elementConfig?.config?.coordenadas?.long
        )
      )
        continue;
      const lat = parseFloat(m.elementConfig.config.coordenadas.lat);
      const lng = parseFloat(m.elementConfig.config.coordenadas.long);
      const fid = m.id;
      id2Marker.set(fid, m);

      const hostData = hostsDataMap?.get(m.id);
      const pingColor = hostData?.length
        ? hostData[0].ping === "1"
          ? theme.palette.color?.status?.up
          : theme.palette.color?.status?.down
        : theme.palette.text.disabled;

      features.push({
        type: "Feature",
        geometry: { type: "Point", coordinates: [lng, lat] },
        properties: {
          __id: fid,
          color: pingColor,
        },
      });
    }

    const sc = new Supercluster({
      radius: Math.max(1, Math.round(maxClusterRadius)), // px
      maxZoom: Math.max(0, Math.floor(MIN_ZOOM_CLUSTER)), // clusteriza até esse zoom
      map: (props) => {
        const color = props.color || "#ffffff";
        return { countByColor: { [color]: 1 }, total: 1 };
      },
      reduce: (acc, props) => {
        acc.countByColor = { ...(acc.countByColor || {}) };
        for (const [color, n] of Object.entries(props.countByColor || {})) {
          acc.countByColor[color] = (acc.countByColor[color] || 0) + n;
        }
        acc.total = (acc.total || 0) + (props.total || 0);
      },
    });

    sc.load(features);
    scRef.current = sc;
    id2MarkerRef.current = id2Marker;
  }, [
    markers,
    enableClustering,
    maxClusterRadius,
    MIN_ZOOM_CLUSTER,
    hostsDataMap,
    theme,
  ]);

  /* ---------- FUNÇÃO: atualiza clusters (viewport + zoom) ---------- */
  const updateClusters = useCallback(() => {
    if (!map) return setClusters([]);

    if (
      !enableClustering ||
      map.getZoom() > MIN_ZOOM_CLUSTER ||
      !scRef.current
    ) {
      setClusters(
        markers.map((marker) => {
          const hostData = hostsDataMap?.get(marker.id);
          const color = hostData?.length
            ? hostData[0].ping === "1"
              ? theme.palette.color?.status?.up
              : theme.palette.color?.status?.down
            : theme.palette.text.disabled;
          return {
            id: marker.id,
            center: [
              parseFloat(marker?.elementConfig?.config?.coordenadas?.lat || 0),
              parseFloat(marker?.elementConfig?.config?.coordenadas?.long || 0),
            ],
            markers: [marker],
            colorGroups: { [color]: 1 },
            isSingle: true,
          };
        }),
      );
      return;
    }

    const b = map.getBounds();
    const bbox = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
    const z = Math.max(0, Math.round(map.getZoom()));

    const raw = scRef.current.getClusters(bbox, z);

    const next = raw.map((f) => {
      const [lng, lat] = f.geometry.coordinates;
      if (f.properties.cluster) {
        const count = f.properties.point_count || f.properties.total || 0;
        const colorGroups = f.properties.countByColor || {};
        return {
          id: `cluster_${f.id}`,
          center: [lat, lng],
          count,
          colorGroups,
          isSingle: false,
        };
      } else {
        const fid = f.properties.__id;
        const marker = id2MarkerRef.current.get(fid);
        const hostData = hostsDataMap?.get(fid);
        const color = hostData?.length
          ? hostData[0].ping === "1"
            ? theme.palette.color?.status?.up
            : theme.palette.color?.status?.down
          : theme.palette.text.disabled;
        return {
          id: marker?.id ?? fid,
          center: [lat, lng],
          markers: marker ? [marker] : [],
          colorGroups: { [color]: 1 },
          isSingle: true,
        };
      }
    });

    setClusters(next);
  }, [map, enableClustering, MIN_ZOOM_CLUSTER, markers, hostsDataMap, theme]);

  useEffect(() => {
    if (!map) return;

    updateClusters();
    const handler = () => updateClusters();

    map.on("moveend zoomend", handler);
    return () => {
      map.off("moveend zoomend", handler);
    };
  }, [map, updateClusters]);

  /* ---------- EFFECT: cria overlay uma única vez ---------- */

  useEffect(() => {
    if (!map) return;

    const container = new PIXI.Container();

    container.eventMode = "static";
    container.interactive = true;
    container.interactiveChildren = true;
    container.sortableChildren = true;

    const overlay = L.pixiOverlay(
      (utils) => {
        const scale = utils.getScale();
        const renderer = utils.getRenderer();
        const container = utils.getContainer();
        renderer.reset();

        // Tamanho dinâmico baseado no zoom
        container.children.forEach((c) => {
          if (c.isBg) return;
          let nodeSize = c.isConnection
            ? 2.8 // ficou esse devido a calibração ter sido feita enquanto o marker estava nesse tamanho
            : config?.configuracoes?.ping?.nodeSize;
          const RADIUS_PX = c.isCluster
            ? 0.4 + (parseFloat(nodeSize) || 5) / 20
            : 0.3 + (parseFloat(nodeSize) || 5) / 20;
          const invScale = 1 / scale;

          c.scale.set(RADIUS_PX * invScale);
        });
        renderer.render(container);
      },
      container,
      {
        destroyInteractionManager: false,
      },
    );

    overlay.addTo(map);
    setPixiOverlay(overlay);

    return () => overlay.remove();
  }, [map, config]);

  useEffect(() => {
    const labelTextures = new Map();
    const isLight = colorMode === "light";
    const fillColor = isLight ? "#202020ff" : "#e6e6e6";
    const haloColor = isLight ? "#e6e6e6" : "#202020ff";
    for (const m of markers) {
      const txt = new PIXI.Text(m.label || m.titulo, {
        fontSize: 28,
        fontWeight: "bold",
        fill: fillColor,
        stroke: haloColor,
        strokeThickness: 5,
        lineJoin: "round",
        dropShadow: true,
        dropShadowColor: haloColor,
        dropShadowAlpha: 0.6,
        dropShadowBlur: 4,
        dropShadowDistance: 0,

        align: "center",
        padding: 6,
      });
      txt.resolution = 2;
      // garante que o texto foi renderizado internamente
      if (typeof txt.updateText === "function")
        try {
          txt.updateText();
        } catch (e) {}
      // usa txt.texture diretamente (já é PIXI.Texture) e stringifica a chave
      labelTextures.set(String(m.id), txt.texture);
    }
    setLabelTextures(labelTextures);
  }, [markers, colorMode]);

  /* ---------- EFFECT: desenha sprites ---------- */
  useEffect(() => {
    if (!pixiOverlay || !texturesLoaded) return;

    const { utils } = pixiOverlay;
    const container = utils.getContainer();
    const project = utils.latLngToLayerPoint;

    // sempre que reconstruir, zera o mapa de sprites
    spriteByIdRef.current.clear();

    //limpa apenas os markers and background
    container.children
      .filter((child) => !child.isConnection)
      .forEach((child) => container.removeChild(child));

    drawBackground(container);

    if (!disable) {
      const renderMarkerArgs = (marker) => {
        let updatedMarker = { ...marker };

        const hostData = hostsDataMap?.get(marker.id);
        if (hostData && hostData.length > 0) {
          const data = hostData[0];
          if (data.ping === "1") {
            updatedMarker.tint = theme.palette.color?.status?.up;
            updatedMarker.severity = 0;
          } else {
            updatedMarker.tint = theme.palette.color?.status?.down;
            updatedMarker.severity = 5;
          }
        }

        return {
          m: updatedMarker,
          container,
          project,
          pixiOverlay,
          colorMode,
          editable,
          showLabel,
          elementSelected,
          handleUpdatePositionElement,
          onClick,
          map,
          rulerMode,
          spriteByIdRef,
          explosionsRef,
          configElementDataMap,
          theme,
          labelTexture: labelTextures.get(marker.id),
          editMode,
          tema,
        };
      };

      if (enableClustering && !editMode && map.getZoom() < 18) {
        clusters.forEach((cluster) => {
          if (cluster.isSingle) {
            const marker = cluster.markers[0];
            if (marker && (marker?.id !== elementSelected?.id || !editable)) {
              drawMarker(renderMarkerArgs(marker));
            }
          } else {
            drawCluster(cluster, container, project);
          }
        });
      } else {
        markers
          .filter((marker) => marker?.id !== elementSelected?.id || !editable)
          .forEach((marker) => {
            drawMarker(renderMarkerArgs(marker));
          });
      }
    }

    if (
      editable &&
      elementSelected &&
      elementSelected.tipo !== 3 &&
      elementSelected.tipo !== 4
    ) {
      const iconExists = iconsElements?.some(
        (icon) => icon.id === elementSelected.img,
      );
      const formatelementSelected = {
        ...elementSelected,
        id: elementSelected?.id ?? elementSelected?.tempId,
        position: [
          parseFloat(elementSelected?.elementConfig?.config?.coordenadas?.lat),
          parseFloat(elementSelected?.elementConfig?.config?.coordenadas?.long),
        ],
        iconId: iconExists
          ? elementSelected.img
          : iconsElements?.find(
              (icon) =>
                icon.nome ==
                (elementSelected.tipo === 0
                  ? "_roteador"
                  : elementSelected.tipo === 1
                    ? "_mapa"
                    : "_pop"),
            )?.id, // caso não tenha, recebe o id com base no tipo de elemento
        baseIcon: "marker_circle",
        tint: theme.palette.color?.zabbixSeverty?.selected,
        severity: 1,
        label: elementSelected.titulo ?? elementSelected.id,
      };

      drawMarker({
        m: formatelementSelected,
        container,
        project,
        pixiOverlay,
        colorMode,
        editable,
        showLabel,
        elementSelected,
        handleUpdatePositionElement,
        onClick,
        map,
        rulerMode,
        spriteByIdRef,
        explosionsRef,
        configElementDataMap,
        theme,
        labelTexture: labelTextures.get(formatelementSelected.id),
        editMode,
        tema,
      });
    }

    return () => {
      container.children
        .filter((child) => !child.isConnection)
        .forEach((child) => {
          if (child.destroyFn) child.destroyFn();
          container.removeChild(child);
        });
    };
  }, [
    pixiOverlay,
    texturesLoaded,
    colorMode,
    markers,
    clusters,
    enableClustering,
    map,
    elementSelected,
    editable,
    iconsElements,
    disable,
    showLabel,
    rulerMode,
    editMode,
    configElementDataMap,
    labelTextures,
    configurationMap?.configuracoes?.animacao?.alertas,
    hostsDataMap,
    tema,
  ]);

  const drawBackground = (container) => {
    const bg = new PIXI.Graphics();
    bg.isBg = true;
    const size = 1e10;
    const hitArea = new PIXI.Rectangle(-size, -size, size * 2, size * 2);
    bg.hitArea = hitArea;
    bg.interactive = true;
    bg.eventMode = "static";

    let pointerDownTime = 0;
    const CLICK_THRESHOLD = 250; // ms

    bg.on("mousedown", () => {
      pointerDownTime = Date.now();
    });

    bg.on("pointertap", (e) => {
      const duration = Date.now() - pointerDownTime;
      if (duration < CLICK_THRESHOLD) {
        e.stopPropagation();
        if (typeof onBackgroundClick === "function") {
          onBackgroundClick();
        }
      }
    });

    container.addChild(bg);
  };

  const drawCluster = (cluster, container, project) => {
    if (!(cluster?.center?.[0] && cluster?.center?.[1])) return;

    const point = project(L.latLng(...cluster.center));
    const node = new PIXI.Container();
    node.position.set(point.x, point.y);
    node.isCluster = true;
    node.interactive = true;
    node.buttonMode = true;
    node.markerId = cluster.id;
    node.clusterData = cluster;

    const bgCircle = new PIXI.Graphics();
    bgCircle.beginFill(colorMode === "light" ? 0xffffff : 0x333333, 0.8);
    bgCircle.drawCircle(0, 0, 2);
    bgCircle.endFill();

    const clusterIconSize = config?.configuracoes?.ping?.clusterIconSize ?? 5;
    const scale = 2 * Math.cbrt(clusterIconSize / 5);
    const outerRadius = 16 * scale;
    const innerRadius = 12 * scale;
    const textSize = 9 * 2 * Math.cbrt(clusterIconSize / 5);

    const colors = Object.entries(cluster.colorGroups || {});
    const donut = new PIXI.Graphics();
    let startAngle = -Math.PI / 2;

    colors.sort((a, b) => b[1] - a[1]);
    const totalColorCount = colors.reduce((acc, [, count]) => acc + count, 0);

    if (totalColorCount > 0) {
      if (colors.length > 1) {
        colors.forEach(([color, count]) => {
          const sliceAngle = (count / totalColorCount) * 2 * Math.PI;
          donut.beginFill(PIXI.utils.string2hex(color.replace(/^#/, "")), 1);
          donut.moveTo(0, 0);
          donut.arc(0, 0, outerRadius, startAngle, startAngle + sliceAngle);
          donut.lineTo(0, 0);
          donut.endFill();
          startAngle += sliceAngle;
        });
      } else {
        const color = colors[0][0];
        donut.beginFill(PIXI.utils.string2hex(color.replace(/^#/, "")), 1);
        donut.drawCircle(0, 0, outerRadius);
        donut.endFill();
      }

      donut.beginFill(colorMode === "light" ? 0xffffff : 0x333333, 1);
      donut.drawCircle(0, 0, innerRadius);
      donut.endFill();
      node.addChild(donut);
    }

    const total =
      (typeof cluster.count === "number" && cluster.count) ||
      (Array.isArray(cluster.markers) ? cluster.markers.length : 0) ||
      0;

    const formatCount = (n) => {
      if (n >= 10000) {
        const rounded = Math.round((n / 1000) * 10) / 10;
        return (rounded % 1 === 0 ? rounded.toFixed(0) : rounded) + "k";
      }
      return n.toLocaleString();
    };

    const countText = new PIXI.Text(formatCount(total), {
      fontFamily: "Arial",
      fontSize: textSize,
      fontWeight: "bold",
      fill: colorMode === "light" ? 0x000000 : 0xffffff,
      align: "center",
    });
    countText.anchor.set(0.5);

    node.addChild(bgCircle);
    node.addChild(countText);

    node.on("click", (e) => {
      e.stopPropagation();
      onClusterClick(cluster);
    });

    container.addChild(node);
  };

  useEffect(() => {
    if (!pixiOverlay) return;

    const valueRef = spriteByIdRef.current.values().next().value;
    if (!valueRef) return;

    const baseRadiusPx =
      valueRef.baseRadiusPx ??
      Math.max(
        (valueRef.circle?.width ?? 0) / 2,
        (valueRef.sprite?.width ?? 0) / 2,
      );
    const nodeScale =
      0.3 + (parseFloat(config?.configuracoes?.ping?.nodeSize) || 5) / 20;
    const radiusPx = baseRadiusPx * nodeScale;

    if (Number.isFinite(radiusPx) && radiusPx !== lastRadiusRef.current) {
      lastRadiusRef.current = radiusPx;
      onRadiusChange?.(radiusPx);
    }
  }, [pixiOverlay, config?.configuracoes?.ping?.nodeSize, onRadiusChange]);

  // Animação border
  useEffect(() => {
    const ticker = PIXI.Ticker.shared;
    const spritesRef = spriteByIdRef.current;
    const configMap = configElementDataMap;
    const explosions = explosionsRef.current;

    // Parâmetros de controle
    const blinkSpeed = 0.008;
    const minAlpha = 0.4;

    const animatedIds = new Set(
      [elementSelected?.id ?? elementSelected?.tempId].filter(Boolean),
    );
    const globalTicker = () => {
      const t = ticker.lastTime;
      // === 1️⃣ Escala do elemento selecionado ===
      if (animatedIds.size > 0) {
        const scale = 1 + 0.16 * Math.sin(t / 150);
        for (const id of animatedIds) {
          const s = spritesRef.get(id);
          if (s) {
            s.sprite.scale.set(
              s.sprite.baseScale.x * scale,
              s.sprite.baseScale.y * scale,
            );
            s.circle.scale.set(scale);
            s.circleBase.scale.set(scale);
          }
        }
      }

      if (editable) {
        return;
      }

      if (configurationMap?.configuracoes?.animacao?.alertas != false) {
        // === 2️⃣ Explosões (alertas) ===
        const delta = ticker.deltaMS / 1000;
        for (const [, data] of explosions) {
          data.t += delta;
          const scaleFactor = 1 + data.t * 2;
          data.graphic.scale.set(scaleFactor);
          data.graphic.alpha = Math.max(0, 0.7 - data.t * 0.7);
          if (data.t > 1) {
            data.t = 0;
            data.graphic.scale.set(1);
            data.graphic.alpha = 0.6;
          }
        }
      }

      // === 3️⃣ Piscar bordas ===
      const alpha =
        minAlpha + (1 - minAlpha) * (0.5 + 0.5 * Math.sin(t * blinkSpeed));
      for (const [key, { border }] of spritesRef) {
        let isDownData = configMap.get(key)?.isDown;
        const hostInfo = hostsDataMap?.get(key);

        if (hostInfo && hostInfo.length > 0) {
          if (hostInfo[0].ping !== "1") isDownData = true;
        }

        if (isDownData && !editable) {
          border.alpha = alpha;
        } else {
          border.alpha = 1;
        }
      }
    };
    ticker.add(globalTicker);
    return () => ticker.remove(globalTicker);
  }, [
    configurationMap?.configuracoes?.animacao?.alertas,
    configElementDataMap,
    elementSelected?.id,
    elementSelected?.tempId,
    editable,
    hostsDataMap,
  ]);

  // Esse ticker garante a atualização global por frame do pixiOverlay
  useEffect(() => {
    if (!pixiOverlay) return;
    const ticker = PIXI.Ticker.shared;

    // se já adicionamos antes, remove para evitar duplicados
    if (tickerFantasmaRef.current) {
      try {
        ticker.remove(tickerFantasmaRef.current);
      } catch (e) {}
    }

    const tickerFantasma = () => {
      pixiOverlay.redraw?.();
    };

    // guarda a referência para limpeza futura
    tickerFantasmaRef.current = tickerFantasma;
    ticker.add(tickerFantasma);

    return () => {
      if (tickerFantasmaRef.current) {
        ticker.remove(tickerFantasmaRef.current);
        tickerFantasmaRef.current = null;
      }
    };
  }, [pixiOverlay]);

  return null;
}

/* ---------- fallback: icone SVG azul ---------- */
function getDefaultIcon() {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
<path d="M12.8 32V20.7059H19.2V32H27.2V16.9412H32L16 0L0 16.9412H4.8V32H12.8Z" fill="white"/>
</svg>
`;
  const b64 = btoa(unescape(encodeURIComponent(svg)));
  return `data:image/svg+xml;base64,${b64}`;
}

/* ---------- cluster icon ---------- */
function getClusterIcon() {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
    <circle cx="20" cy="20" r="20" fill="white"/>
  </svg>`;
  const b64 = btoa(unescape(encodeURIComponent(svg)));
  return `data:image/svg+xml;base64,${b64}`;
}

function mapsEqual(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return a === b;
  if (!(a instanceof Map) || !(b instanceof Map)) return false;
  if (a.size !== b.size) return false;

  for (const [key, valA] of a) {
    if (!b.has(key)) return false;
    const valB = b.get(key);
    if (!isEqual(valA, valB)) return false;
  }
  return true;
}

function propsAreEqual(prev, next) {
  if (prev.editable !== next.editable) return false;
  if (prev.disable !== next.disable) return false;
  if (prev.showLabel !== next.showLabel) return false;
  if (prev.zoom !== next.zoom) return false;
  if (prev.editMode !== next.editMode) return false;
  if (prev.rulerMode !== next.rulerMode) return false;

  if (!isEqual(prev.markers, next.markers)) return false;
  if (!isEqual(prev.elementSelected, next.elementSelected)) return false;
  if (!isEqual(prev.iconsElements, next.iconsElements)) return false;
  if (!isEqual(prev.configurationMap, next.configurationMap)) return false;
  if (!mapsEqual(prev.configElementDataMap, next.configElementDataMap))
    return false;

  return true;
}
export default React.memo(PixiOverlay, propsAreEqual);
